import Div100vh from 'react-div-100vh'

import { CharStatus } from '../../lib/statuses'
import { Cell } from '../grid/Cell'

// Why the gate is being shown:
//   'no-invite' -> no token at all (someone found the bare URL).
//   'rejected'  -> a token WAS present but the server refused it (stale/changed
//                  link, or signed with a different/older secret). Different copy
//                  so the guest knows to use their LATEST link, not that they
//                  were never invited.
export type GateReason = 'no-invite' | 'rejected'

// Shown when no (valid-looking) invite token is present, or when the token
// present failed server verification. This is an exclusivity/UX gate, not real
// security — the server still rejects any write without a verified token. It
// just keeps the board hidden from someone who arrived without a working link.
//
// Each state spells its own five-letter word in grey (absent) tiles: a row
// that didn't land, which is exactly what this screen means.
const COPY: Record<GateReason, { word: string; title: string; body: string }> =
  {
    'no-invite': {
      word: 'GUEST',
      title: 'This game needs your invite link.',
      body: 'Every guest got a personal link by email. Open it from that email to play.',
    },
    rejected: {
      word: 'RETRY',
      title: 'This link is out of date.',
      body: 'We sent you a newer one. Open the most recent invite email and use the link there.',
    },
  }

const ABSENT: CharStatus = 'absent'

export const InviteGate = ({
  reason = 'no-invite',
}: {
  reason?: GateReason
}) => {
  const { word, title, body } = COPY[reason]
  return (
    <Div100vh>
      <main className="flex h-full w-full flex-col items-center justify-center bg-white px-6 text-center dark:bg-slate-900">
        <div className="mb-8 flex justify-center" aria-hidden="true">
          {word.split('').map((letter, i) => (
            <Cell
              key={i}
              value={letter}
              status={ABSENT}
              isRevealing
              isCompleted
              position={i}
            />
          ))}
        </div>
        <h1 className="max-w-sm text-3xl font-extrabold leading-tight tracking-tight text-gray-900 [text-wrap:balance] dark:text-white">
          {title}
        </h1>
        <p className="mt-3 max-w-sm text-base leading-relaxed text-gray-600 dark:text-gray-300">
          {body}
        </p>
      </main>
    </Div100vh>
  )
}
